import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { Coordinates, useGraphColors } from "@core";
import { Sensor, useApiContext } from "@core/apis";
import { getWeatherOptions } from "@core/apis/Weather";

import {
  BaseSignatureDiagramChart,
  SignatureSeriesData,
  SignatureYAxisConfig,
} from "../charts/BaseSignatureDiagramChart";
import {
  buildWeatherMap,
  filterToDateRange,
  toSignatureDataPoints,
} from "../charts/signatureDiagramUtils";
import type { RawDataPoint } from "../charts/signatureDiagramUtils";
import type { DateRange, Resolution } from "../shared/types";

type FormattedSensorPoint = {
  datetime?: string;
  timestamp: number;
  temperature?: number;
  humidity?: number;
};

function flattenSensorDataToRaw(
  sensorData: Array<{ uid: string; data: FormattedSensorPoint[] }>,
  dataType: "temperature" | "humidity"
): RawDataPoint[] {
  return sensorData.flatMap((sensor) =>
    sensor.data
      .map((point) => {
        const value = dataType === "temperature" ? point.temperature : point.humidity;
        if (value === undefined) return null;
        const datetime =
          typeof point.datetime === "string"
            ? point.datetime
            : new Date(point.timestamp).toISOString();
        return { datetime, value };
      })
      .filter((p): p is RawDataPoint => p !== null)
  );
}

type IndoorClimateSignatureDiagramContainerProps = {
  sensors: Sensor[];
  selectedSensors?: string[];
  dateRange: DateRange;
  coordinates?: Coordinates;
  resolution: Resolution;
  chartHeight?: number;
  isLoading?: boolean;
};

/**
 * IndoorClimateSignatureDiagramContainer - Signature diagram for indoor climate
 *
 * Displays indoor temperature and humidity scatter plots against outdoor temperature.
 * Fetches weather data internally and transforms sensor data to signature format.
 */
export const IndoorClimateSignatureDiagramContainer: React.FC<
  IndoorClimateSignatureDiagramContainerProps
> = ({
  sensors,
  selectedSensors = [],
  dateRange,
  coordinates = { lat: 59.911491, lon: 10.757933 },
  resolution = "hourly",
  chartHeight,
  isLoading: externalLoading,
}) => {
  const { t } = useTranslation(["propai", "propai_properties"]);
  const graphColors = useGraphColors();
  const { weatherApi } = useApiContext();

  // Fetch outdoor temperature data
  const { data: outdoorData, isLoading: outdoorDataLoading } = useQuery(
    getWeatherOptions(weatherApi, {
      coordinates,
      startDate: dateRange.startDate.toISO(),
      endDate: dateRange.endDate.toISO(),
    })
  );

  // Format weather data
  const formattedWeatherData = useMemo(() => {
    if (!outdoorData || outdoorData.length === 0) return [];
    return outdoorData.map((point: { datetime: string; value: number }) => ({
      datetime: point.datetime,
      value: point.value,
      timestamp: new Date(point.datetime).getTime(),
    }));
  }, [outdoorData]);

  // Filter sensors to living_air_temperature type and selected UIDs
  const filteredSensors = useMemo(() => {
    if (!sensors?.length) return [];

    let filtered = sensors.filter((s) => s.sensor_type_technical_name === "living_air_temperature");
    if (selectedSensors.length > 0) {
      filtered = filtered.filter((s) => selectedSensors.includes(s.uid));
    }
    return filtered;
  }, [sensors, selectedSensors]);

  // Extract and format sensor data
  const formattedSensorData = useMemo(() => {
    if (!filteredSensors?.length) return [];

    return filteredSensors.map((sensor) => ({
      uid: sensor.uid,
      data:
        sensor.data
          ?.map((reading: any) => ({
            datetime: reading.timestamp || reading.datetime,
            temperature:
              reading.values?.living_air_temperature === undefined
                ? reading.value
                : reading.values.living_air_temperature,
            humidity: reading.values?.indoor_relative_humidity,
            timestamp: new Date(reading.timestamp || reading.datetime).getTime(),
          }))
          .filter((reading: any) => reading.temperature !== undefined) || [],
    }));
  }, [filteredSensors]);

  // Build signature data points using shared aggregation/filter helpers
  const signatureData = useMemo(() => {
    const hasData = formattedSensorData.length > 0 && formattedWeatherData.length > 0;
    if (hasData) {
      const weatherRaw: RawDataPoint[] = formattedWeatherData.map((p) => ({
        datetime: p.datetime,
        value: p.value,
      }));
      const weatherMap = buildWeatherMap(weatherRaw, resolution);
      const tempPoints = filterToDateRange(
        flattenSensorDataToRaw(formattedSensorData, "temperature"),
        dateRange
      );
      const humidPoints = filterToDateRange(
        flattenSensorDataToRaw(formattedSensorData, "humidity"),
        dateRange
      );
      return {
        primaryData: toSignatureDataPoints(tempPoints, weatherMap, resolution),
        secondaryData: toSignatureDataPoints(humidPoints, weatherMap, resolution),
      };
    }
    return { primaryData: [], secondaryData: [] };
  }, [formattedSensorData, formattedWeatherData, resolution, dateRange]);

  // Map to SignatureSeriesData[]
  const series = useMemo((): SignatureSeriesData[] => {
    const result: SignatureSeriesData[] = [];

    if (signatureData.primaryData.length > 0) {
      result.push({
        uid: "temp",
        name: t("average_indoor_temperature"),
        data: signatureData.primaryData,
        unit: "°C",
        color: graphColors[0],
        yAxisIndex: 0,
        visible: true,
      });
    }

    if (signatureData.secondaryData.length > 0) {
      result.push({
        uid: "humid",
        name: t("average_indoor_humidity"),
        data: signatureData.secondaryData,
        unit: "%",
        color: graphColors[1],
        yAxisIndex: 1,
        visible: true,
      });
    }

    return result;
  }, [signatureData, t]);

  // Y-axis configs
  const yAxisConfigs = useMemo((): SignatureYAxisConfig[] => {
    const configs: SignatureYAxisConfig[] = [
      {
        title: { text: `${t("average_indoor_temperature")} (°C)` },
        labels: { align: "right", x: -15 },
      },
    ];

    if (signatureData.secondaryData.length > 0) {
      configs.push({
        title: { text: `${t("average_indoor_humidity")} (%)` },
        labels: { align: "right", x: -15 },
      });
    }

    return configs;
  }, [t, signatureData]);

  return (
    <BaseSignatureDiagramChart
      series={series}
      yAxisConfigs={yAxisConfigs}
      chartHeight={chartHeight}
      chartId="indoor-climate-signature"
      resolution={resolution}
      isLoading={outdoorDataLoading || externalLoading}
      allowSeriesToggle={true}
      yAxisLayout="stacked"
    />
  );
};
